export type ProfesorDetalle = {
  nombre: string;
  rol?: string;
  cargo?: string;
  tipoClase?: string;
};

export const PROFESOR_ROLE_GROUPS = [
  { rol: "profesor", titulo: "Profesor/a" },
  { rol: "profesor adjunto", titulo: "Profesor/a adjunto" },
  { rol: "auxiliar", titulo: "Auxiliar" },
] as const;

export { isNombreADesignar } from "../../utils";

const compareNombre = (a: ProfesorDetalle, b: ProfesorDetalle) => {
  const nameA = a.nombre.toUpperCase();
  const nameB = b.nombre.toUpperCase();
  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  return 0;
};

export const getProfesoresByRol = (detalles: ProfesorDetalle[] = [], rol: string) =>
  detalles.filter((profe) => profe?.rol?.toLowerCase?.() === rol).sort(compareNombre);

export const getPrioritizedData = (
  transformedData: any[],
  turno: string | undefined,
  creditos: number | undefined,
  esCarreraDeGrado: boolean | null,
  modalidad: string | undefined,
  isMediumScreen: boolean = false
) => {
  const dataMap: Record<string, any> = {};
  transformedData.forEach((item) => {
    dataMap[item.titulo] = item;
  });

  const tieneCreditos = creditos !== undefined && creditos > 0;

  const maxColumns = isMediumScreen ? 4 : 5;
  const columnData: Record<number, any> = {};

  for (let i = 1; i <= maxColumns; i++) {
    columnData[i] = null;
  }

  const datosUsados = new Set<string>();

  const asignarColumna = (columna: number, clave: string, esForzado: boolean = false) => {
    if (columna > maxColumns || datosUsados.has(clave)) return false;
    if (dataMap[clave]) {
      columnData[columna] = { ...dataMap[clave], columna: columna }; 
      datosUsados.add(clave);
      return true;
    } else if (esForzado) {
      columnData[columna] = { titulo: clave, value: null, detalles: [], columna: columna }; 
      return true;
    }
    return false;
  };

  const asignarColumnaPrioridad = (columna: number, opciones: string[], esForzado: boolean = false) => {
    for (const opcion of opciones) {
      if (asignarColumna(columna, opcion)) return true;
    }
    if (esForzado) {
      columnData[columna] = { titulo: opciones[0], value: null, detalles: [], columna: columna }; 
      return true;
    }
    return false;
  };

  if (esCarreraDeGrado) {

    if (dataMap["Profesor"]) {
      columnData[1] = { ...dataMap["Profesor"], isProfesor: true, columna: 1 }; 
      datosUsados.add("Profesor");
    } else {
      columnData[1] = { titulo: "Profesor", value: null, detalles: [], isProfesor: true, columna: 1 }; 
    }

    asignarColumnaPrioridad(2, ["Teórica", "Teórica-Práctica", "Práctica 1", "Práctica 2", "Práctica 3"], true);

    if (maxColumns === 5) {

      asignarColumnaPrioridad(3, ["Teórica-Práctica", "Práctica 1", "Práctica 2", "Práctica 3"]);

      if (tieneCreditos) {

        if (turno) {
          columnData[4] = { titulo: "Turno", value: turno, detalles: [], isTurno: true, columna: 4 }; 
        } else {
          asignarColumnaPrioridad(4, ["Práctica 1", "Práctica 2", "Práctica 3"]);
        }
      } else {

        if (!asignarColumnaPrioridad(4, ["Práctica 1", "Práctica 2", "Práctica 3"])) {
          if (turno) {
            columnData[4] = { titulo: "Turno", value: turno, detalles: [], isTurno: true, columna: 4 }; 
          }
        }
      }

      if (tieneCreditos) {
        columnData[5] = { titulo: "Créditos", value: creditos, detalles: [], isCreditos: true, columna: 5 }; 
      } else if (turno && !columnData[4]?.isTurno) {
        columnData[5] = { titulo: "Turno", value: turno, detalles: [], isTurno: true, columna: 5 }; 
      } else {
        asignarColumnaPrioridad(5, ["Práctica 2", "Práctica 3"]);
      }
    } else {

      if (tieneCreditos) {

        if (turno) {
          columnData[3] = { titulo: "Turno", value: turno, detalles: [], isTurno: true, columna: 3 }; 
        } else {
          asignarColumnaPrioridad(3, ["Práctica 1", "Práctica 2", "Práctica 3"]);
        }
      } else {

        if (!asignarColumnaPrioridad(3, ["Teórica-Práctica", "Práctica 1", "Práctica 2", "Práctica 3"])) {
          if (turno) {
            columnData[3] = { titulo: "Turno", value: turno, detalles: [], isTurno: true, columna: 3 }; 
          }
        }
      }

      if (tieneCreditos) {
        columnData[4] = { titulo: "Créditos", value: creditos, detalles: [], isCreditos: true, columna: 4 }; 
      } else if (turno && !columnData[3]?.isTurno) {
        columnData[4] = { titulo: "Turno", value: turno, detalles: [], isTurno: true, columna: 4 }; 
      } else {
        asignarColumnaPrioridad(4, ["Práctica 1", "Práctica 2", "Práctica 3"]);
      }
    }
  } else {

    if (dataMap["Profesor"]) {
      columnData[1] = { ...dataMap["Profesor"], isProfesor: true, columna: 1 }; 
      datosUsados.add("Profesor");
    } else {
      columnData[1] = { titulo: "Profesor", value: null, detalles: [], isProfesor: true, columna: 1 }; 
    }

    asignarColumnaPrioridad(2, ["Teórica", "Teórica-Práctica", "Práctica 1"], true);

    if (maxColumns === 5) {

      if (tieneCreditos) {
        columnData[3] = { titulo: "Créditos", value: creditos, detalles: [], isCreditos: true, columna: 3 }; 
      } else {
        asignarColumnaPrioridad(3, ["Práctica 2"]);
      }

      if (modalidad) {
        columnData[4] = { titulo: "Modalidad", value: modalidad, detalles: [], isModalidad: true, columna: 4 }; 
      } else {
        asignarColumnaPrioridad(4, ["Práctica 3"]);
      }

      asignarColumna(5, "Fechas", true);
    } else {

      if (tieneCreditos) {
        columnData[3] = { titulo: "Créditos", value: creditos, detalles: [], isCreditos: true, columna: 3 }; 
      } else if (modalidad) {
        columnData[3] = { titulo: "Modalidad", value: modalidad, detalles: [], isModalidad: true, columna: 3 }; 
      } else {
        asignarColumnaPrioridad(3, ["Práctica 1", "Práctica 2"]);
      }

      asignarColumna(4, "Fechas", true);
    }
  }

  Object.values(columnData).forEach((item) => {
    if (item && item.detalles && item.detalles.length > 2) {
      item.hasMore = true;
      item.allDetalles = [...item.detalles];
      item.detalles = item.detalles.slice(0, 2);
    }
  });

  const todosLosHorarios = ["Teórica", "Teórica-Práctica", "Práctica 1", "Práctica 2", "Práctica 3"].filter(h => dataMap[h]);
  const horariosIncluidos = ["Teórica", "Teórica-Práctica", "Práctica 1", "Práctica 2", "Práctica 3"].filter(h => datosUsados.has(h));

  if (todosLosHorarios.length > horariosIncluidos.length) {

    let ultimoHorarioVisible: string | null = null;

    const ordenHorarios = {
      "Teórica": 1,
      "Teórica-Práctica": 2,
      "Práctica 1": 3,
      "Práctica 2": 4,
      "Práctica 3": 5
    } as const;

    type TipoHorario = keyof typeof ordenHorarios;

    let maxOrden = -1;
    horariosIncluidos.forEach(horario => {
      if (ordenHorarios[horario as TipoHorario] > maxOrden) {
        maxOrden = ordenHorarios[horario as TipoHorario];
        ultimoHorarioVisible = horario;
      }
    });

    if (ultimoHorarioVisible) {
      Object.values(columnData).forEach((item) => {
        if (item && item.titulo === ultimoHorarioVisible) {
          item.hasMore = true;
        }
      });
    }
  }

  const finalItems = Object.entries(columnData)
    .sort(([a], [b]) => parseInt(a) - parseInt(b))
    .map(([columnaStr, item]) => item)
    .filter((item) => item !== null);

  return {
    items: finalItems,
    hasMoreItems: Object.keys(dataMap).some(key => !datosUsados.has(key))
  };
};